import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

export default function PasswordChangeModal({ isOpen, onClose, onSubmit, isLoading }) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!newPassword) {
      newErrors.newPassword = t('passwordChange.errors.newPasswordRequired');
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t('passwordChange.errors.passwordMinLength');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(newPassword)) {
      newErrors.newPassword = t('passwordChange.errors.passwordComplexity');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('passwordChange.errors.confirmPasswordRequired');
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('passwordChange.errors.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({ newPassword, confirmPassword });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden transform transition-all">
        <div className="h-2 bg-gradient-to-r from-[#FF8B4C] via-[#FFC857] to-[#FFE9A0]"></div>
        
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-r from-[#FF8B4C] to-[#FFC857] rounded-full p-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
              {t('passwordChange.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('passwordChange.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('passwordChange.newPassword')}
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#FF8B4C] focus:border-transparent"
                  placeholder="••••••••"
                />
               <button
  type="button"
  onClick={() => setShowNewPassword(!showNewPassword)}
  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
  aria-label={
    showNewPassword
      ? t('passwordChange.hidePassword')
      : t('passwordChange.showPassword')
  }
  title={
    showNewPassword
      ? t('passwordChange.hidePassword')
      : t('passwordChange.showPassword')
  }
>
  {showNewPassword ? (
    <EyeSlashIcon className="w-5 h-5" />
  ) : (
    <EyeIcon className="w-5 h-5" />
  )}
</button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('passwordChange.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#FF8B4C] focus:border-transparent"
                  placeholder="••••••••"
                />
              <button
  type="button"
  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
  aria-label={
    showConfirmPassword
      ? t('passwordChange.hideConfirmPassword')
      : t('passwordChange.showConfirmPassword')
  }
  title={
    showConfirmPassword
      ? t('passwordChange.hideConfirmPassword')
      : t('passwordChange.showConfirmPassword')
  }
>
  {showConfirmPassword ? (
    <EyeSlashIcon className="w-5 h-5" />
  ) : (
    <EyeIcon className="w-5 h-5" />
  )}
</button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('passwordChange.requirements')}
              </p>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full ${newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  {t('passwordChange.minLength')}
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full ${/[A-Z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  {t('passwordChange.uppercase')}
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full ${/[a-z]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  {t('passwordChange.lowercase')}
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full ${/\d/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  {t('passwordChange.number')}
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-4 h-4 rounded-full ${/[@$!%*?&]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                  {t('passwordChange.specialChar')}
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#FF8B4C] to-[#FFC857] hover:from-[#FFA06D] hover:to-[#FFD985] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF8B4C] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('passwordChange.changing')}
                </div>
              ) : (
                t('passwordChange.changePassword')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}